<template>
  <div
    class="group relative mb-4 overflow-hidden rounded-2xl border-2 border-solid transition-all hover:scale-[1.02]"
    :class="
      isToday
        ? 'shadow-yellow-hard-lg animate-pulse border-yellow-500 bg-white dark:bg-kekkle-dark-light'
        : 'hover:shadow-yellow-hard-lg border-black-dark bg-white shadow-yellow-hard dark:bg-kekkle-dark-light'
    "
  >
    <!-- Decorative elements -->
    <div
      class="absolute right-2 top-2 text-4xl"
      :class="isToday ? 'animate-bounce opacity-80' : 'opacity-20'"
    >
      🎂
    </div>
    <div
      class="absolute bottom-2 left-2 text-2xl"
      :class="isToday ? 'opacity-60' : 'opacity-20'"
    >
      🎉
    </div>
    <!-- Special confetti for today's birthdays -->
    <div
      v-if="isToday"
      class="absolute left-1/2 top-2 -translate-x-1/2 text-3xl opacity-70"
    >
      ✨
    </div>

    <div class="relative flex items-center gap-4 p-4">
      <!-- Profile Picture -->
      <div class="relative shrink-0">
        <div
          class="border-3 h-16 w-16 overflow-hidden rounded-full border-black-dark bg-white shadow-md transition-transform group-hover:scale-110"
        >
          <img
            v-if="profilePicture"
            class="h-full w-full object-cover"
            :src="profilePicture"
            alt="Profile picture"
            draggable="false"
          />
          <div
            v-else
            class="flex h-full w-full items-center justify-center bg-gradient-to-br from-yellow-200 to-pink-200 text-2xl"
          >
            🎈
          </div>
        </div>
        <!-- Birthday badge -->
        <div
          class="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-black-dark bg-yellow-400 shadow-sm"
        >
          <span class="text-xs">🎂</span>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1">
        <div class="flex items-baseline gap-2">
          <h2
            class="text-lg font-bold leading-tight"
            :class="
              isToday
                ? 'text-orange-900 dark:text-orange-100'
                : 'text-gray-900 dark:text-slate-100'
            "
          >
            {{ name ?? "" }}
          </h2>
          <span class="text-xl">🥳</span>
        </div>
        <p
          v-if="isToday"
          class="text-base font-bold text-orange-600 dark:text-orange-400"
        >
          {{ $t("components.birthday.is_birthday_today") }}
        </p>
        <p v-else class="text-sm font-medium text-pink-600 dark:text-pink-400">
          {{ $t("components.birthday.is_birthday") }}
        </p>
        <p
          v-if="birthdayDate"
          class="mt-1 text-xs font-medium"
          :class="
            isToday
              ? 'text-orange-700 dark:text-orange-300'
              : 'text-gray-700 dark:text-slate-300'
          "
        >
          {{ birthdayDateText }}
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { ImageSizes } from "@/enums/images-sizes";

export default {
  name: "Birthday",
  props: {
    uuid: {
      type: String,
      required: true,
    },
    birthdayDate: {
      type: Number,
      required: false,
      default: null,
    },
    isToday: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup() {
    const groupsStore = useGroupsStore();
    const userStore = useUserStore();
    return { groupsStore, userStore };
  },
  computed: {
    groupData() {
      return this.groupsStore.groupData;
    },
    activeGroupId() {
      return this.userStore.activeGroup;
    },
    user() {
      return this.groupData?.members?.[this.uuid];
    },
    name() {
      return this.user?.name;
    },
    profilePicture() {
      return this.groupsStore.groupMembersProfilePictureData?.[
        this.activeGroupId
      ]?.[this.uuid]?.[ImageSizes.HD]?.url;
    },
    birthdayDateText() {
      if (!this.birthdayDate || !this.user?.birthday) {
        return "";
      }

      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      const currentDay = today.getDate();

      // Create birthday date for this year
      const birthdayThisYear = new Date(
        currentYear,
        this.user.birthday.getMonth(),
        this.birthdayDate,
      );

      // Calculate days until birthday
      const todayMidnight = new Date(currentYear, currentMonth, currentDay);
      const birthdayMidnight = new Date(
        currentYear,
        this.user.birthday.getMonth(),
        this.birthdayDate,
      );
      const diffTime = birthdayMidnight - todayMidnight;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Format the date
      const monthName = this.$t(
        `global.months.${this.user.birthday.toLocaleDateString("en-US", { month: "long" }).toLowerCase()}`,
      );

      if (diffDays === 0) {
        return `${this.$t("components.birthday.today")} 🎉 (${monthName} ${this.birthdayDate})`;
      } else if (diffDays === 1) {
        return `${this.$t("components.birthday.tomorrow")} (${monthName} ${this.birthdayDate})`;
      } else if (diffDays > 0) {
        return `${this.$t("components.birthday.in_days", { days: diffDays })} (${monthName} ${this.birthdayDate})`;
      } else {
        return `${monthName} ${this.birthdayDate}`;
      }
    },
  },
  watch: {
    uuid() {
      if (!this.profilePicture) {
        this.groupsStore.downloadGroupMemberProfilePictureData({
          uuid: this.uuid,
          format: ImageSizes.HD,
        });
      }
    },
  },
  created() {
    if (!this.profilePicture) {
      this.groupsStore.downloadGroupMemberProfilePictureData({
        uuid: this.uuid,
        format: ImageSizes.HD,
      });
    }
  },
};
</script>
